"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import type { PipelineCandidate } from "@/lib/api/types";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";

interface PipelineSelectionContextValue {
  allSelected: boolean;
  someSelected: boolean;
  pageCount: number;
  totalCount: number;
  isSelected: (candidateId: string) => boolean;
  onSelectPage: () => void;
  onSelectAllCandidates: () => void;
  onClearSelection: () => void;
  onCheckboxChange: (candidateId: string, index: number, e: React.MouseEvent) => void;
  onRowClick: (candidate: PipelineCandidate, index: number, e: React.MouseEvent) => void;
}

const PipelineSelectionContext = createContext<PipelineSelectionContextValue | null>(null);

export function usePipelineSelection() {
  const ctx = useContext(PipelineSelectionContext);
  if (!ctx) {
    throw new Error(
      "usePipelineSelection must be used within <PipelineSelectionProvider>",
    );
  }
  return ctx;
}

interface ProviderProps {
  candidates: PipelineCandidate[];
  totalCount: number;
  onCandidateClick: (candidate: PipelineCandidate) => void;
  onSelectAllCandidates?: () => void;
  children: ReactNode;
}

export function PipelineSelectionProvider({
  candidates,
  totalCount,
  onCandidateClick,
  onSelectAllCandidates,
  children,
}: ProviderProps) {
  const {
    bulkMode,
    selectedCandidateIds,
    excludedCandidateIds,
    selectionTotal,
    selectedStageId,
    filterSearch,
    filterSignature,
    toggleCandidate,
    mergeSelected,
    selectAllCandidates,
    clearSelection,
  } = usePipelineViewStore();

  const isAllMode = bulkMode === "all";
  const isSelected = useCallback(
    (id: string) =>
      isAllMode ? !excludedCandidateIds.has(id) : selectedCandidateIds.has(id),
    [isAllMode, excludedCandidateIds, selectedCandidateIds],
  );

  const lastClickedIdx = useRef<number | null>(null);

  // Selection lives in a global store, so it otherwise survives
  // client-side navigation — reopening the pipeline would re-hydrate a
  // stale selection (auto-checking rows + popping the bulk toolbar with no
  // user action). Reset it whenever the pipeline table mounts/unmounts so
  // every visit starts clean. ``clearSelection`` is a stable zustand
  // setter, so this runs exactly once per mount.
  // Changing stage or any cohort filter redefines the candidate universe,
  // so a virtual `all` selection (or stacked page picks) no longer matches
  // what's on screen — reset whenever those change. `filterSignature` is the
  // primitive mirror of the filter panel's selections; the filter object itself
  // would be a new identity on every render.
  useEffect(() => {
    clearSelection();
  }, [selectedStageId, filterSearch, filterSignature, clearSelection]);

  useEffect(() => {
    clearSelection();
    return () => clearSelection();
  }, [clearSelection]);

  const handleShiftSelect = useCallback(
    (index: number) => {
      if (lastClickedIdx.current === null) return false;
      const start = Math.min(lastClickedIdx.current, index);
      const end = Math.max(lastClickedIdx.current, index);
      const rangeIds = candidates
        .slice(start, end + 1)
        .map((c) => c.job_candidate_id);
      // mergeSelected, not selectAll: in all-mode `selectedCandidateIds` is
      // empty (selection is "everything minus exclusions"), so rebuilding from
      // it would silently drop a select-all of 500 down to the shift-clicked
      // few. mergeSelected un-excludes instead.
      mergeSelected(rangeIds);
      lastClickedIdx.current = index;
      return true;
    },
    [candidates, mergeSelected],
  );

  const onCheckboxChange = useCallback(
    (candidateId: string, index: number, e: React.MouseEvent) => {
      if (e.shiftKey && handleShiftSelect(index)) return;
      toggleCandidate(candidateId);
      lastClickedIdx.current = index;
    },
    [toggleCandidate, handleShiftSelect],
  );

  const onRowClick = useCallback(
    (candidate: PipelineCandidate, index: number, e: React.MouseEvent) => {
      const target = e.target;
      const row = e.currentTarget;
      if (!(target instanceof Node) || !row.contains(target)) {
        return;
      }
      if (
        target instanceof Element &&
        target.closest(
          "button, a[href], input, textarea, select, label, [role='button'], [role='checkbox'], [role='switch'], [role='menuitem'], [role='dialog']",
        )
      ) {
        return;
      }
      if (e.shiftKey && lastClickedIdx.current !== null) {
        handleShiftSelect(index);
        return;
      }
      onCandidateClick(candidate);
    },
    [handleShiftSelect, onCandidateClick],
  );

  const selectedCount = isAllMode
    ? Math.max(selectionTotal - excludedCandidateIds.size, 0)
    : selectedCandidateIds.size;
  const someSelected = selectedCount > 0;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && someSelected) {
        clearSelection();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [someSelected, clearSelection]);

  const onSelectPage = useCallback(() => {
    // Stack onto prior pages — do not replace the whole selection.
    mergeSelected(candidates.map((c) => c.job_candidate_id));
  }, [candidates, mergeSelected]);

  const allSelected =
    candidates.length > 0 && candidates.every((c) => isSelected(c.job_candidate_id));

  const value = useMemo<PipelineSelectionContextValue>(
    () => ({
      allSelected,
      someSelected,
      pageCount: candidates.length,
      totalCount,
      isSelected,
      onSelectPage,
      onSelectAllCandidates:
        onSelectAllCandidates ?? (() => selectAllCandidates(totalCount)),
      onClearSelection: clearSelection,
      onCheckboxChange,
      onRowClick,
    }),
    [
      allSelected,
      someSelected,
      candidates.length,
      totalCount,
      isSelected,
      onSelectPage,
      onSelectAllCandidates,
      selectAllCandidates,
      clearSelection,
      onCheckboxChange,
      onRowClick,
    ],
  );

  return (
    <PipelineSelectionContext.Provider value={value}>
      {children}
    </PipelineSelectionContext.Provider>
  );
}
